import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { buildInquiryMailto } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { useLang } from "../i18n";

const EMPTY = {
  name: "",
  email: "",
  company: "",
  country: "",
  species: "",
  volume: "",
  message: "",
};

const Field = ({ id, label, children }) => (
  <div className="flex flex-col gap-2">
    <Label
      htmlFor={id}
      className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-slate"
    >
      {label}
    </Label>
    {children}
  </div>
);

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 py-3 text-lg text-brand-blue placeholder:text-brand-slate/40 shadow-none focus-visible:border-brand-timber focus-visible:ring-0";

export const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const { t } = useLang();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("quote.errFill"));
      return;
    }
    window.location.href = buildInquiryMailto(form);
    toast.success(t("quote.opening"));
  };

  return (
    <section
      id="quote"
      data-testid="quote-form"
      className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40"
    >
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="lg:col-span-5"
        >
          <motion.span
            variants={fadeUp}
            className="font-mono text-xs uppercase tracking-[0.25em] text-brand-timber"
          >
            {t("quote.kicker")}
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-4 font-serif text-5xl font-medium leading-[0.95] tracking-tight text-brand-blue md:text-6xl"
          >
            {t("quote.title")}
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-6 max-w-md font-light text-base leading-relaxed text-brand-slate"
          >
            {t("quote.sub")}
          </motion.p>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-6 lg:col-start-7"
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <motion.div variants={fadeUp}>
              <Field id="name" label={t("quote.name")}>
                <Input data-testid="quote-input-name" id="name" value={form.name} onChange={set("name")} placeholder={t("quote.phName")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Field id="email" label={t("quote.email")}>
                <Input data-testid="quote-input-email" id="email" type="email" value={form.email} onChange={set("email")} placeholder={t("quote.phEmail")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Field id="company" label={t("quote.company")}>
                <Input data-testid="quote-input-company" id="company" value={form.company} onChange={set("company")} placeholder={t("quote.phCompany")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Field id="country" label={t("quote.country")}>
                <Input data-testid="quote-input-country" id="country" value={form.country} onChange={set("country")} placeholder={t("quote.phCountry")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Field id="species" label={t("quote.species")}>
                <Input data-testid="quote-input-species" id="species" value={form.species} onChange={set("species")} placeholder={t("quote.phSpecies")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Field id="volume" label={t("quote.volume")}>
                <Input data-testid="quote-input-volume" id="volume" value={form.volume} onChange={set("volume")} placeholder={t("quote.phVolume")} className={inputCls} />
              </Field>
            </motion.div>
            <motion.div variants={fadeUp} className="md:col-span-2">
              <Field id="message" label={t("quote.message")}>
                <Textarea data-testid="quote-input-message" id="message" value={form.message} onChange={set("message")} rows={4} placeholder={t("quote.phMessage")} className={inputCls + " resize-none"} />
              </Field>
            </motion.div>
          </div>

          <motion.div variants={fadeUp} className="mt-10">
            <button
              type="submit"
              data-testid="quote-submit-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-blue px-9 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand-bone transition-colors duration-300 hover:bg-brand-timber"
            >
              {t("quote.submit")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </motion.form>
      </div>
    </section>
  );
};
